export type WebExtensionStatus = "checking" | "open" | "update" | "get";

export type ExtensionInstalledResponse =
  | boolean
  | {
      installed: boolean;
      version?: string;
    }
  | null
  | undefined;

export const shouldForceOutdatedStatus = (search: string): boolean => {
  const value = new URLSearchParams(search).get("outdated");
  return value?.trim().toLowerCase() === "true";
};

export const getChromeExtensionSettingsUrl = (extensionId: string): string =>
  `chrome://extensions/?id=${encodeURIComponent(extensionId.trim())}`;

const parseVersion = (version: string): number[] | null => {
  const normalized = version.trim().replace(/^v/i, "").split("-")[0];
  if (!/^\d+(?:\.\d+)*$/.test(normalized)) return null;
  return normalized.split(".").map(Number);
};

export const compareVersions = (left: string, right: string): number | null => {
  const leftParts = parseVersion(left);
  const rightParts = parseVersion(right);
  if (!leftParts || !rightParts) return null;

  const length = Math.max(leftParts.length, rightParts.length);
  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? 0;
    const rightPart = rightParts[index] ?? 0;
    if (leftPart > rightPart) return 1;
    if (leftPart < rightPart) return -1;
  }

  return 0;
};

export const resolveWebExtensionStatus = (
  response: ExtensionInstalledResponse,
  minimumVersion: string,
): WebExtensionStatus => {
  if (!response) return "get";
  if (response === true) return "open";
  if (typeof response === "object" && !response.installed) return "get";

  const installedVersion =
    typeof response === "object" ? response.version : undefined;
  if (!installedVersion || !minimumVersion.trim()) return "open";

  const comparison = compareVersions(installedVersion, minimumVersion);
  return comparison !== null && comparison < 0 ? "update" : "open";
};
